/**
 * Frontend human-in-the-loop tool for selecting a numeric parameter.
 *
 * Registered via makeAssistantTool so assistant-ui:
 *   1. Sends the tool definition in RunAgentInput.tools
 *   2. Sets status to "requires-action" for human input
 *   3. Enables addResult() to send the user's value back
 */
import { useState } from "react";
import { makeAssistantTool } from "@assistant-ui/react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { Button } from "~/components/ui/button";
import { Slider } from "~/components/ui/slider";
import { ToolCard } from "./ToolCard";

interface ParameterSliderArgs {
  title: string;
  description: string;
  min: number;
  max: number;
  step: number;
  default_value: number;
  unit: string;
}

const ParameterSliderRender: ToolCallMessagePartComponent<ParameterSliderArgs, string> = ({
  args,
  result,
  status,
  addResult,
}) => {
  const min = args?.min ?? 0;
  const max = args?.max ?? 100;
  const step = args?.step ?? 1;
  const unit = args?.unit ?? "";

  // Lazy init: null until the user drags, then falls back to args.default_value.
  // useState only reads its initializer on mount, so we can't rely on it for
  // streaming args that arrive after first render.
  const [value, setValue] = useState<number | null>(null);
  const resolvedValue = value ?? args?.default_value ?? min;

  const isRunning = status.type === "running";
  const isSubmitted = result != null;

  const handleConfirm = () => {
    const display = unit ? `${resolvedValue} ${unit}` : `${resolvedValue}`;
    addResult(`The user selected: ${display}. Respond with text only. Do NOT call any tool.`);
  };

  // Submitted state
  if (isSubmitted) {
    const display = unit ? `${resolvedValue} ${unit}` : `${resolvedValue}`;
    return (
      <ToolCard
        label="Parameter Slider"
        color="green"
        isLoading={false}
        loadingText=""
        isSubmitted={true}
      >
        {args?.title && <p className="font-medium text-sm">{args.title}</p>}
        <p className="text-sm font-medium text-green-700">{display}</p>
      </ToolCard>
    );
  }

  return (
    <ToolCard
      label="Parameter Slider"
      color="green"
      isLoading={isRunning || !args}
      loadingText="Generating parameter..."
      isSubmitted={false}
    >
      {args?.title && <p className="font-medium text-sm">{args.title}</p>}
      {args?.description && <p className="text-xs text-muted-foreground">{args.description}</p>}

      <div className="space-y-3 pt-1">
        <Slider
          min={min}
          max={max}
          step={step}
          value={[resolvedValue]}
          onValueChange={([v]) => {
            if (v !== undefined) setValue(v);
          }}
        />

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {min}
            {unit ? ` ${unit}` : ""}
          </span>
          <span className="text-sm font-medium text-green-700">
            {resolvedValue}
            {unit ? ` ${unit}` : ""}
          </span>
          <span>
            {max}
            {unit ? ` ${unit}` : ""}
          </span>
        </div>
      </div>

      <Button size="sm" onClick={handleConfirm} className="mt-1">
        Confirm
      </Button>
    </ToolCard>
  );
};

// Intersection with Record<string, unknown> needed because makeAssistantTool's
// generic constraint requires an index signature that interfaces don't provide.
export const ParameterSliderTool = makeAssistantTool<
  ParameterSliderArgs & Record<string, unknown>,
  string
>({
  toolName: "present_parameter_slider",
  type: "human",
  description:
    "Present a slider for the user to select a numeric parameter value. " +
    "The user adjusts the slider and confirms their selection.",
  parameters: {
    type: "object",
    properties: {
      title: { type: "string", description: "Heading for the parameter slider" },
      description: {
        type: "string",
        description: "Context about what this parameter controls",
      },
      min: { type: "number", description: "Minimum slider value" },
      max: { type: "number", description: "Maximum slider value" },
      step: { type: "number", description: "Step increment for the slider" },
      default_value: {
        type: "number",
        description: "Initial value the slider starts at",
      },
      unit: {
        type: "string",
        description: "Optional unit label (e.g., '%', 'rows', 'seconds')",
      },
    },
    required: ["title", "min", "max", "step", "default_value"],
  },
  render: ParameterSliderRender,
});
